import { useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight, X, Link2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

/**
 * Banner shown on Lead Detail when the backend detected possible duplicates.
 * Lets the user navigate to a candidate, open the merge dialog, or dismiss.
 */
export default function PossibleDuplicatesBanner({ lead, onMergeClick, onDismiss }) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const navigate = useNavigate();
  const dups = lead?.possible_duplicates || [];
  if (!dups.length) return null;

  const dismiss = async () => {
    if (!window.confirm(`Dismiss the duplicate hint for this lead?\n\nThe banner won't show again, but the linked leads will still be visible. You can re-run duplicate detection at any time.`)) return;
    try {
      // Persist dismiss server-side by clearing the field
      await axios.put(`${API}/leads/${lead.id}`, { custom_fields: { ...(lead.custom_fields || {}), _dup_dismissed: "1" } });
      // Optimistically remove from UI
      onDismiss && onDismiss();
      toast.success("Dismissed duplicate hint");
    } catch {
      toast.error("Failed to dismiss");
    }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="duplicate-banner">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
          <AlertTriangle className="w-4 h-4 text-amber-700" />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-amber-900 mb-1">Maybe same as {dups.length === 1 ? "another lead" : `${dups.length} other leads`}</h3>
          <p className="text-xs text-amber-800 mb-3">Decide whether to merge or keep them separate.</p>

          <div className="space-y-2">
            {dups.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 bg-white border border-amber-200 rounded-lg" data-testid={`dup-candidate-${d.id}`}>
                <button
                  type="button"
                  onClick={() => navigate(`/leads/${d.id}`)}
                  className="flex-1 min-w-0 text-left group"
                  data-testid={`dup-goto-${d.id}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900 truncate">{d.name || "—"}</span>
                    {d.company && <span className="text-xs text-gray-500 truncate">· {d.company}</span>}
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                      {Math.round((d.confidence || 0) * 100)}% match
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5 truncate">{d.reason}</p>
                  {(d.phone || d.location) && (
                    <p className="text-[10px] text-gray-400 mt-0.5 truncate">
                      {d.phone || "—"}{d.location ? ` · ${d.location}` : ""}
                    </p>
                  )}
                </button>

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => onMergeClick && onMergeClick(d)}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-teal-700 hover:text-teal-900 px-2 py-1 border border-teal-300 rounded-md hover:bg-teal-50"
                    data-testid={`dup-merge-${d.id}`}
                  >
                    <Link2 className="w-3 h-3" /> Merge
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/leads/${d.id}`)}
                    className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-50"
                    title="View lead"
                    data-testid={`dup-view-${d.id}`}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={dismiss}
          className="p-1 text-amber-600 hover:text-amber-900 rounded hover:bg-amber-100 shrink-0"
          title="Dismiss"
          data-testid="dup-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
